import { useRef } from 'react';

import * as Sentry from '@sentry/react';

import type { ConditionalRequestOptions, ConditionalResult } from '@/services/http';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** The shape every registry list page shares: the server's version token
 * travels with the rows so a poll can revalidate with `If-None-Match`. */
export interface RegistryPage {
  total: number;
  version: string;
}

/** A page and the query it answers. */
interface Landed<P> {
  key: string;
  page: P;
}

/** One conditional read of a page for `query`. */
export type RegistryPageReader<Q, P> = (
  query: Q,
  options: ConditionalRequestOptions
) => Promise<ConditionalResult<P>>;

/**
 * A page of a registry list, polled with ETag revalidation. Shared by the
 * order queue and the unmatched-mail list, which differ only in what they
 * read and what they do to a row afterwards.
 *
 * The query is a `refetchDeps` entry, so a filter or page change drops the
 * stored ETag: a different filter is a different token space, and reusing
 * the token would 304 against the previous filter's rows.
 *
 * The poll keeps its last payload across such a restart, so the previous
 * query's rows stay on screen until the new ones land. `isLoading` is true
 * for that stretch as well: an empty previous page says nothing about the
 * query now being read.
 */
export function useRegistryPage<Q, P extends RegistryPage>(
  query: Q,
  read: RegistryPageReader<Q, P>,
  interval: number,
  empty: P
) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;
  const readRef = useRef(read);
  readRef.current = read;

  const poll = usePoll<Landed<P>>({
    interval,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const sent = queryRef.current;
      const result = await readRef.current(sent, { signal, etag: manual ? null : etag });
      return result === 'not-modified'
        ? UNCHANGED
        : withEtag({ key: JSON.stringify(sent), page: result.data }, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const landed = poll.data;
  const awaitingOwnPage = landed !== undefined && landed.key !== key && poll.error === undefined;

  return {
    page: landed?.page ?? empty,
    isLoading: poll.isLoading || awaitingOwnPage,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}
